import React, { useState, useEffect, useCallback, useRef } from 'react';
import { vendorService } from '../../../services/api';
import { fetchCitiesForState } from '../../../services/locationService';
import { 
  Search, User, CheckCircle, AlertCircle, Clock, Trash2, X, Plus, 
  ChevronDown, ChevronUp, Loader, AlertTriangle, RotateCw
} from 'lucide-react';

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", 
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", 
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", 
  "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", 
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

const getPageNumbers = (currentPage, totalPages) => {
  const maxVisiblePages = 5;
  const pages = [];
  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);
    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);
    if (currentPage <= 2) {
      end = 3;
    } else if (currentPage >= totalPages - 3) {
      start = totalPages - 4;
    }
    if (start > 1) {
      pages.push('ellipsis1');
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }
    pages.push(totalPages - 1);
  }
  return pages;
};

const VendorListingManagerTab = ({ toast }) => {
  // Lists & Autocomplete
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [brands, setBrands] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [draftsPagination, setDraftsPagination] = useState({ page: 0, totalPages: 0 });
  const [draftsPageSize, setDraftsPageSize] = useState(10);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  // Form State
  const [listingType, setListingType] = useState('COMPANY'); // 'COMPANY' vs 'INDIVIDUAL'
  const [isEditMode, setIsEditMode] = useState(false);
  const [listingId, setListingId] = useState(null);
  const [vendorMembership, setVendorMembership] = useState('FREE');
  const [companyName, setCompanyName] = useState('');
  const [aboutCompany, setAboutCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [stateName, setStateName] = useState('');
  const [cityName, setCityName] = useState('');
  const [availableCities, setAvailableCities] = useState([]);

  useEffect(() => {
    let isMounted = true;
    if (stateName && stateName.trim()) {
      fetchCitiesForState(stateName.trim()).then(cities => {
        if (isMounted) {
          setAvailableCities(cities || []);
        }
      }).catch(err => {
        console.error("Error loading cities for state:", err);
        if (isMounted) setAvailableCities([]);
      });
    } else {
      setAvailableCities([]);
    }
    return () => { isMounted = false; };
  }, [stateName]);

  const handleStateSelectChange = (e) => {
    const selectedState = e.target.value;
    setStateName(selectedState);
    setCityName('');
  };
  const [zipcode, setZipcode] = useState('');
  const [area, setArea] = useState('');
  const [website, setWebsite] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [gstin, setGstin] = useState('');
  const [establishedSince, setEstablishedSince] = useState('');
  const [instagram, setInstagram] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [facebook, setFacebook] = useState('');
  const [twitter, setTwitter] = useState('');

  // Business Hours
  const [bhType, setBhType] = useState('MON_TO_SAT');
  const [bhMainOpen, setBhMainOpen] = useState('09:00 AM');
  const [bhMainClose, setBhMainClose] = useState('06:00 PM');
  const [bhSatOpen, setBhSatOpen] = useState('09:00 AM');
  const [bhSatClose, setBhSatClose] = useState('06:00 PM');
  const [bhSunOpen, setBhSunOpen] = useState('09:00 AM');
  const [bhSunClose, setBhSunClose] = useState('06:00 PM');
  const [bhCustom, setBhCustom] = useState([]);

  // File Upload States
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [govtProofFile, setGovtProofFile] = useState(null);
  const [govtProofPreview, setGovtProofPreview] = useState('');
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [existingGalleryUrls, setExistingGalleryUrls] = useState([]);

  // UI Loaders/Status
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingListing, setLoadingListing] = useState(false);
  const [submittingDraft, setSubmittingDraft] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [cancelDraftId, setCancelDraftId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [touched, setTouched] = useState({});

  // Deletion modal states
  const [hasBeenApproved, setHasBeenApproved] = useState(false);
  const [showAdminDeleteStep1Modal, setShowAdminDeleteStep1Modal] = useState(false);
  const [showAdminDeleteStep2Modal, setShowAdminDeleteStep2Modal] = useState(false);
  const [adminDeleteConfirmInput, setAdminDeleteConfirmInput] = useState('');
  const [adminDeletingInProgress, setAdminDeletingInProgress] = useState(false);

  const validate = () => {
    const errors = {};

    if (listingType === 'INDIVIDUAL') {
      if (!companyName || !companyName.trim()) {
        errors.companyName = "Name is required.";
      } else if (companyName.trim().length < 2) {
        errors.companyName = "Name must be at least 2 characters.";
      }

      if (!phone || !phone.trim()) {
        errors.phone = "Contact number is required.";
      } else if (!/^\d{10,12}$/.test(phone.trim())) {
        errors.phone = "Contact number must be 10, 11 or 12 digits.";
      }

      if (!email || !email.trim()) {
        errors.email = "Email address is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        errors.email = "Please enter a valid email address.";
      }

      if (!stateName || !stateName.trim()) {
        errors.stateName = "State is required.";
      }

      if (!cityName || !cityName.trim()) {
        errors.cityName = "City is required.";
      }

      if (!area || !area.trim()) {
        errors.area = "Area is required.";
      }

      if (!categoryId) {
        errors.categoryId = "Category is required.";
      }

      if (!isEditMode && !govtProofFile && !govtProofPreview) {
        errors.govtProofFile = "Legal Document proof (Aadhaar Card / PAN Card) is required.";
      }

      return errors;
    }

    if (!companyName || !companyName.trim()) {
      errors.companyName = "Company name is required.";
    } else if (companyName.trim().length < 2) {
      errors.companyName = "Company name must be at least 2 characters.";
    }

    if (!aboutCompany || !aboutCompany.trim()) {
      errors.aboutCompany = "About company description is required.";
    } else if (aboutCompany.trim().length < 10) {
      errors.aboutCompany = "About company description must be at least 10 characters.";
    }

    if (!phone || !phone.trim()) {
      errors.phone = "Contact number is required.";
    } else if (!/^\d{10,12}$/.test(phone.trim())) {
      errors.phone = "Contact number must be 10, 11 or 12 digits.";
    }

    if (!email || !email.trim()) {
      errors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = "Please enter a valid email address.";
    }

    if (!address || !address.trim()) {
      errors.address = "Address is required.";
    }

    if (!area || !area.trim()) {
      errors.area = "Area is required.";
    }

    if (!stateName || !stateName.trim()) {
      errors.stateName = "State is required.";
    } else {
      const validState = INDIAN_STATES.find(s => s.toLowerCase() === stateName.trim().toLowerCase());
      if (!validState) {
        errors.stateName = "Please select a valid State from the list.";
      }
    }

    if (!cityName || !cityName.trim()) {
      errors.cityName = "City is required.";
    }

    if (!zipcode || !zipcode.trim()) {
      errors.zipcode = "Zipcode is required.";
    } else if (!/^\d{6}$/.test(zipcode.trim())) {
      errors.zipcode = "Zipcode must be exactly 6 digits.";
    }

    if (gstin && gstin.trim()) {
      if (!/^[a-zA-Z0-9]{15}$/.test(gstin.trim())) {
        errors.gstin = "GSTIN must be exactly 15 alphanumeric characters.";
      }
    }

    if (!categoryId) {
      errors.categoryId = "Category is required.";
    }

    if (categoryId && (!selectedTags || selectedTags.length === 0)) {
      errors.selectedTags = "Please select at least one Tag for your category.";
    }

    if (!isEditMode && !bannerFile) {
      errors.bannerFile = "Profile Banner image is required.";
    }

    if (!logoFile && !logoPreview) {
      errors.logoFile = "Company Logo image is required.";
    }

    if (!govtProofFile && !govtProofPreview) {
      errors.govtProofFile = "Government Certificate / GST Photo Proof is required.";
    }

    if (establishedSince && (parseInt(establishedSince) < 1800 || parseInt(establishedSince) > new Date().getFullYear())) {
      errors.establishedSince = `Established since must be a valid year between 1800 and ${new Date().getFullYear()}.`;
    }

    return errors;
  };

  const fieldErrors = validate();

  const getInputClass = (fieldName, isTextArea = false) => {
    const hasError = touched[fieldName] && fieldErrors[fieldName];
    return `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none transition-all duration-200 bg-white ${
      hasError 
        ? 'border-rose-500 focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500' 
        : 'border-slate-200 focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391]'
    } ${isTextArea ? 'min-h-[100px] resize-y' : ''}`;
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  // Fetch initial data
  const loadVendors = useCallback(async () => {
    setLoadingVendors(true);
    try {
      const res = await vendorService.getAdminVendorsForListingManager();
      if (res && res.success && res.data) {
        setVendors(res.data);
      } else {
        setVendors([]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch vendor users list.');
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  }, [toast]);

  const loadCategories = useCallback(async (type = listingType) => {
    try {
      const res = await vendorService.getCategories(true, type);
      if (res && res.success && res.data) {
        setCategories(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [listingType]);

  const loadDrafts = useCallback(async (page = 0, size = draftsPageSize) => {
    try {
      const res = await vendorService.getAdminListingDrafts({}, page, size);
      if (res && res.success && res.data) {
        setDrafts(res.data.drafts || []);
        setDraftsPagination({ page: res.data.currentPage, totalPages: res.data.totalPages });
      }
    } catch (err) {
      console.error(err);
    }
  }, [draftsPageSize]);

  useEffect(() => {
    loadVendors();
    loadCategories();
    loadDrafts();
  }, [loadVendors, loadCategories, loadDrafts]);

  // Click outside to close autocomplete suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load tags & brands when category changes
  useEffect(() => {
    if (!categoryId) {
      setTags([]);
      setBrands([]);
      return;
    }
    const fetchTagsAndBrands = async () => {
      try {
        const tagRes = await vendorService.getTagsByCategory(categoryId);
        if (tagRes && tagRes.success) setTags(tagRes.data || []);
        
        const brandRes = await vendorService.getBrandsByCategory(categoryId);
        if (brandRes && brandRes.success) setBrands(brandRes.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchTagsAndBrands();
  }, [categoryId]);

  // Filter vendors by search term
  const filteredVendors = Array.isArray(vendors) ? vendors.filter(v => 
    (v.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (v.emailId || '').toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // Handle vendor selection
  const handleSelectVendor = async (vendor) => {
    setSelectedVendor(vendor);
    setLoadingListing(true);
    resetFormFields();

    try {
      const res = await vendorService.getAdminVendorListing(vendor.emailId);
      if (res && res.success && res.data && res.data.hasListing) {
        // Edit mode
        const list = res.data.listing;
        const lType = (list.listingType || 'COMPANY').toUpperCase();
        setListingType(lType);
        loadCategories(lType);
        setIsEditMode(true);
        setListingId(list.id);
        setHasBeenApproved(list.hasBeenApproved || false);
        setVendorMembership(list.membershipPlan || 'FREE');
        setCompanyName(list.companyName || '');
        setAboutCompany(list.aboutCompany || '');
        setPhone(list.phone || '');
        setEmail(list.email || '');
        setAddress(list.address || '');
        setStateName(list.state || '');
        setCityName(list.city || '');
        setZipcode(list.zipcode || '');
        setArea(list.area || '');
        setWebsite(list.website || '');
        setCategoryId(list.categoryId || '');
        setGstin(list.gstin || '');
        setEstablishedSince(list.establishedSince || '');
        setInstagram(list.instagram || '');
        setLinkedin(list.linkedin || '');
        setFacebook(list.facebook || '');
        setTwitter(list.twitter || '');
        
        // Tags/brands mapping
        if (list.tags) setSelectedTags(list.tags.map(t => t.id));
        if (list.brands) setSelectedBrands(list.brands.map(b => b.id));

        // Previews of existing files
        if (list.profileBannerUrl) setBannerPreview(getImageUrl(list.profileBannerUrl));
        if (list.logoUrl) setLogoPreview(getImageUrl(list.logoUrl));
        if (list.govtProofUrl) setGovtProofPreview(getImageUrl(list.govtProofUrl));
        if (list.galleryImageUrls) setExistingGalleryUrls(list.galleryImageUrls);

        // Business Hours mapping
        if (list.businessHours) {
          const bh = list.businessHours;
          setBhType(bh.type || 'MON_TO_SAT');
          if (bh.mainHours) {
            setBhMainOpen(bh.mainHours.openTime || '09:00 AM');
            setBhMainClose(bh.mainHours.closeTime || '06:00 PM');
          }
          if (bh.saturdayHours) {
            setBhSatOpen(bh.saturdayHours.openTime || '09:00 AM');
            setBhSatClose(bh.saturdayHours.closeTime || '06:00 PM');
          }
          if (bh.sundayHours) {
            setBhSunOpen(bh.sundayHours.openTime || '09:00 AM');
            setBhSunClose(bh.sundayHours.closeTime || '06:00 PM');
          }
          setBhCustom(bh.customHours || []);
        }
      } else {
        // Create mode
        setIsEditMode(false);
        setListingId(null);
        setVendorMembership('FREE'); // Defaults to FREE for new creations
        setEmail(vendor.emailId || '');
        setPhone(vendor.mobileNo || '');
        setCompanyName(vendor.name || '');
        setStateName(vendor.state || '');
        setCityName(vendor.city || '');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch listing data.');
    } finally {
      setLoadingListing(false);
    }
  };

  const resetFormFields = () => {
    setIsEditMode(false);
    setListingId(null);
    setCompanyName('');
    setAboutCompany('');
    setPhone('');
    setEmail('');
    setAddress('');
    setStateName('');
    setCityName('');
    setZipcode('');
    setArea('');
    setWebsite('');
    setCategoryId('');
    setSelectedTags([]);
    setSelectedBrands([]);
    setGstin('');
    setEstablishedSince('');
    setInstagram('');
    setLinkedin('');
    setFacebook('');
    setTwitter('');
    setBannerFile(null);
    setBannerPreview('');
    setLogoFile(null);
    setLogoPreview('');
    setGovtProofFile(null);
    setGovtProofPreview('');
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setExistingGalleryUrls([]);
    setBhType('MON_TO_SAT');
    setBhMainOpen('09:00 AM');
    setBhMainClose('06:00 PM');
    setBhSatOpen('09:00 AM');
    setBhSatClose('06:00 PM');
    setBhSunOpen('09:00 AM');
    setBhSunClose('06:00 PM');
    setBhCustom([]);
    setHasBeenApproved(false);
    setTouched({});
  };

  const handleAdminDeleteVendorListing = async () => {
    if (!listingId) return;
    if (adminDeleteConfirmInput.trim().toUpperCase() !== 'DELETE') {
      toast.error("Please type DELETE to confirm permanent deletion.");
      return;
    }
    setAdminDeletingInProgress(true);
    try {
      const res = await vendorService.adminDeleteVendorListing(listingId);
      if (res && res.success) {
        toast.success("Vendor listing and all associated media files permanently deleted successfully.");
        setShowAdminDeleteStep2Modal(false);
        setShowAdminDeleteStep1Modal(false);
        setAdminDeleteConfirmInput('');
        resetFormFields();
        setSelectedVendor(null);
        loadVendors();
      } else {
        toast.error(res.error || "Failed to delete vendor listing.");
      }
    } catch (err) {
      console.error("Error admin deleting vendor listing:", err);
      toast.error(err.response?.data?.detail || "Failed to delete vendor listing.");
    } finally {
      setAdminDeletingInProgress(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    return `${base}${url}`;
  };

  // Image Upload Helpers
  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleGovtProofChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setGovtProofFile(file);
      setGovtProofPreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    setGalleryFiles(prev => [...prev, ...files]);
    const filePreviews = files.map(file => URL.createObjectURL(file));
    setGalleryPreviews(prev => [...prev, ...filePreviews]);
  };

  const removeSelectedGalleryFile = (index) => {
    setGalleryFiles(prev => prev.filter((_, i) => i !== index));
    setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingGalleryFile = (url) => {
    setExistingGalleryUrls(prev => prev.filter(u => u !== url));
  };

  // Tag & Brand selection helpers
  const handleTagToggle = (id) => {
    setSelectedTags(prev => 
      prev.includes(id) ? prev.filter(tId => tId !== id) : [...prev, id]
    );
  };

  const handleBrandToggle = (id) => {
    setSelectedBrands(prev => 
      prev.includes(id) ? prev.filter(bId => bId !== id) : [...prev, id]
    );
  };

  // Business Hours custom helpers
  const addCustomHourRow = () => {
    setBhCustom(prev => [...prev, { day: 'Monday', openTime: '09:00 AM', closeTime: '06:00 PM' }]);
  };

  const removeCustomHourRow = (index) => {
    setBhCustom(prev => prev.filter((_, i) => i !== index));
  };

  const updateCustomHourRow = (index, key, val) => {
    setBhCustom(prev => prev.map((row, i) => i === index ? { ...row, [key]: val } : row));
  };

  // Submit Draft Action
  const handleSubmitDraftClick = (e) => {
    e.preventDefault();

    setTouched({
      companyName: true,
      aboutCompany: true,
      phone: true,
      email: true,
      address: true,
      area: true,
      stateName: true,
      cityName: true,
      zipcode: true,
      gstin: true,
      categoryId: true,
      selectedTags: true,
      bannerFile: true,
      logoFile: true,
      govtProofFile: true,
      establishedSince: true
    });

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      toast.warning("Please correct the validation errors in the form.");
      return;
    }

    if (bhType === 'CUSTOM') {
      const validCustomHours = bhCustom.filter(ch => ch.day && ch.day !== 'Day');
      if (validCustomHours.length === 0) {
        toast.warning("For Custom Business Hours, at least one day is required.");
        return;
      }
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmitDraft = async () => {
    setShowConfirmModal(false);
    setSubmittingDraft(true);

    try {
      const bh = {
        type: bhType,
        mainHours: bhType !== 'CUSTOM' ? { openTime: bhMainOpen, closeTime: bhMainClose } : null,
        saturdayHours: bhType === 'MON_TO_FRI' ? null : (bhType === 'MON_TO_SAT' ? { openTime: bhSatOpen, closeTime: bhSatClose } : null),
        sundayHours: bhType === 'ALL_DAYS' ? { openTime: bhSunOpen, closeTime: bhSunClose } : null,
        customHours: bhType === 'CUSTOM' ? bhCustom : []
      };

      const payload = {
        vendorEmail: selectedVendor.emailId,
        actionType: isEditMode ? 'UPDATE' : 'CREATE',
        listingId: listingId,
        listingType: listingType,
        companyName,
        aboutCompany,
        phone,
        email,
        address,
        state: stateName,
        city: cityName,
        zipcode,
        area: area || null,
        website: website || null,
        categoryId: parseInt(categoryId),
        tagIds: selectedTags,
        brandIds: selectedBrands,
        gstin,
        establishedSince: establishedSince ? parseInt(establishedSince) : null,
        businessHours: bh,
        instagram: instagram || null,
        linkedin: linkedin || null,
        facebook: facebook || null,
        twitter: twitter || null,
        galleryImageUrls: existingGalleryUrls
      };

      const formData = new FormData();
      formData.append('listing', JSON.stringify(payload));
      
      if (bannerFile) formData.append('profileBanner', bannerFile);
      if (logoFile) formData.append('logo', logoFile);
      if (govtProofFile) formData.append('govtProof', govtProofFile);
      
      galleryFiles.forEach(file => {
        formData.append('galleryImages', file);
      });

      const res = await vendorService.adminSubmitVendorListingDraft(formData);
      if (res && res.success) {
        toast.success(`Draft submitted successfully. Vendor ${selectedVendor.emailId} has been notified via email.`);
        resetFormFields();
        setSelectedVendor(null);
        loadDrafts();
        loadVendors(); // Refresh status flags
      }
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail || 'Failed to submit listing draft to vendor.';
      toast.error(detail);
    } finally {
      setSubmittingDraft(false);
    }
  };

  const handleCancelDraftClick = (draftId) => {
    setCancelDraftId(draftId);
  };

  const handleCancelDraftConfirm = async () => {
    if (!cancelDraftId) return;
    try {
      const res = await vendorService.cancelAdminListingDraft(cancelDraftId);
      if (res && res.success) {
        toast.success('Draft withdrawn successfully.');
        loadDrafts();
        loadVendors();
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to cancel the draft.');
    } finally {
      setCancelDraftId(null);
    }
  };

  // Time Options list
  const timeOptions = [];
  const periods = ['AM', 'PM'];
  for (let h = 1; h <= 12; h++) {
    for (let m = 0; m < 60; m += 30) {
      periods.forEach(p => {
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        timeOptions.push(`${hh}:${mm} ${p}`);
      });
    }
  }

  return (
    <div className="p-6 bg-slate-50 min-h-screen text-slate-700">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Title */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Vendor Listing Manager</h1>
            <p className="text-sm text-slate-500">Create or update listings directly on behalf of vendors. Proposed updates require vendor approval.</p>
          </div>
        </div>

        {/* Top Search Bar (Only shown if no vendor is selected) */}
        {!selectedVendor ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-3" ref={searchRef}>
            <label className="block text-sm font-bold text-slate-800 flex items-center gap-2">
              <Search className="w-4 h-4 text-[#2ab391]" />
              1. Search and Select Registered Vendor
            </label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search registered vendor by name, email, or mobile..."
                  value={searchTerm}
                  onFocus={() => setShowSuggestions(true)}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSuggestions(true);
                  }}
                  className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] focus:bg-white"
                />
              </div>

              {/* Floating Autocomplete Dropdown */}
              {showSuggestions && (
                <div className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg divide-y divide-slate-100">
                  {loadingVendors ? (
                    <div className="py-6 flex justify-center items-center">
                      <Loader className="w-5 h-5 animate-spin text-[#2ab391]" />
                    </div>
                  ) : filteredVendors.length === 0 ? (
                    <p className="text-center py-4 text-xs text-slate-400">No matching vendor users found.</p>
                  ) : (
                    filteredVendors.map(vendor => (
                      <button
                        key={vendor.id}
                        type="button"
                        onClick={() => {
                          handleSelectVendor(vendor);
                          setShowSuggestions(false);
                          setSearchTerm('');
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 text-slate-700 transition flex justify-between items-center gap-2"
                      >
                        <div className="truncate">
                          <p className="font-semibold text-sm truncate">{vendor.name}</p>
                          <p className="text-xs text-slate-400 truncate mt-0.5">{vendor.emailId} &bull; {vendor.mobileNo || 'No Mobile'}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {vendor.hasListing ? (
                            <span className="text-[9px] px-1.5 py-0.5 font-bold uppercase rounded bg-emerald-50 text-emerald-700 border border-emerald-100">Live</span>
                          ) : (
                            <span className="text-[9px] px-1.5 py-0.5 font-bold uppercase rounded bg-slate-50 text-slate-500 border border-slate-100">No Listing</span>
                          )}
                          {vendor.hasPendingDraft && (
                            <span className="text-[8px] px-1.5 py-0.5 font-semibold bg-amber-50 text-amber-700 border border-amber-100 rounded">Pending Approval</span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Selected Vendor Banner (When a vendor is active) */
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-sm shrink-0">
                {selectedVendor.name ? selectedVendor.name.charAt(0).toUpperCase() : 'V'}
              </div>
              <div className="text-left">
                <span className="block text-xs uppercase font-bold text-emerald-600 tracking-wider">Active Vendor Selected</span>
                <span className="text-sm font-bold text-slate-800">{selectedVendor.name} </span>
                <span className="text-xs text-slate-500">({selectedVendor.emailId} &bull; {selectedVendor.mobileNo || 'No Mobile'})</span>
              </div>
            </div>
            <div className="flex items-center gap-2 self-end sm:self-center">
              <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase border ${
                selectedVendor.hasListing ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'
              }`}>
                {selectedVendor.hasListing ? 'Live' : 'No Listing'}
              </span>
              {selectedVendor.hasListing && isEditMode && hasBeenApproved && (
                <button
                  type="button"
                  onClick={() => setShowAdminDeleteStep1Modal(true)}
                  title="Permanently delete this vendor's listing & media"
                  className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-lg shadow-2xs transition flex items-center gap-1.5 cursor-pointer active:scale-95"
                >
                  <Trash2 size={13} />
                  Delete Vendor Listing
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setSelectedVendor(null);
                  resetFormFields();
                }}
                className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700 rounded-lg transition"
              >
                Change Vendor
              </button>
            </div>
          </div>
        )}

        {/* 2. Listing Details Draft Card */}
        {!selectedVendor ? (
          <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-12 text-center text-slate-400">
            <User className="w-12 h-12 mx-auto text-slate-300 mb-2" />
            <p className="text-base font-semibold">Search and select a vendor above to start.</p>
            <p className="text-xs mt-1">You will be able to edit their existing directory listing or create a new one from scratch.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="p-4 bg-slate-100 border-b border-slate-200 font-semibold text-slate-800 flex justify-between items-center gap-2">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-slate-500" />
                2. Listing Details Draft
              </span>
              <span className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase ${
                isEditMode ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {isEditMode ? 'Edit Mode' : 'Create Mode'}
              </span>
            </div>

            {loadingListing ? (
              <div className="p-16 flex flex-col justify-center items-center gap-2 text-slate-500">
                <Loader className="w-8 h-8 animate-spin text-[#2ab391]" />
                <span className="text-sm font-medium">Loading vendor listing data...</span>
              </div>
            ) : (
              <form onSubmit={handleSubmitDraftClick} className="p-6 space-y-6">
                
                {/* Alert for Pending draft */}
                {selectedVendor.hasPendingDraft && (
                  <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg flex items-start gap-2 text-xs">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Vendor has an active pending approval request.</p>
                      <p>You cannot submit another draft until they either Approve or Reject the current one, or you cancel it in the History below.</p>
                    </div>
                  </div>
                )}

                {/* Listing Type Selection */}
                <div className="p-4 bg-teal-50/60 border border-teal-200/80 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-2xs">
                  <div>
                    <label className="block text-xs font-bold text-teal-900 uppercase tracking-wide flex items-center gap-2">
                      Listing Type
                      {isEditMode && (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-teal-100 text-teal-800 border border-teal-200">
                          Locked
                        </span>
                      )}
                    </label>
                    <p className="text-[11px] text-teal-700 font-medium">
                      {isEditMode
                        ? `This vendor is registered for ${listingType === 'INDIVIDUAL' ? 'Individual Business Listing' : 'Company Listing'}.`
                        : 'Select whether this vendor is a Company Listing or an Individual Business Listing.'}
                    </p>
                  </div>
                  <select
                    value={listingType}
                    disabled={isEditMode}
                    onChange={(e) => {
                      const val = e.target.value;
                      setListingType(val);
                      setCategoryId('');
                      setSelectedTags([]);
                      setSelectedBrands([]);
                      loadCategories(val);
                    }}
                    className="bg-white border border-teal-300 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391] disabled:bg-gray-100 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <option value="COMPANY">Company Listing</option>
                    <option value="INDIVIDUAL">Individual Business Listing</option>
                  </select>
                </div>

                {listingType === 'INDIVIDUAL' ? (
                  /* Individual Business Form Layout for Admin */
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* 1. Name */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Name *</label>
                        <input
                          type="text"
                          required
                          placeholder="Full Name / Business Title"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          onBlur={() => handleBlur('companyName')}
                          className={getInputClass('companyName')}
                        />
                        {touched.companyName && fieldErrors.companyName && (
                          <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                            <AlertCircle size={12} className="shrink-0" /> {fieldErrors.companyName}
                          </p>
                        )}
                      </div>

                      {/* 2. Target Vendor Email */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Target Vendor Email (Readonly)</label>
                        <input
                          type="text"
                          disabled
                          value={email}
                          className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-400 font-medium"
                        />
                      </div>

                      {/* 3. Phone Number */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Phone Number *</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. 9876543210"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          onBlur={() => handleBlur('phone')}
                          className={getInputClass('phone')}
                        />
                        {touched.phone && fieldErrors.phone && (
                          <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                            <AlertCircle size={12} className="shrink-0" /> {fieldErrors.phone}
                          </p>
                        )}
                      </div>

                      {/* 4. Category Dropdown */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Category *</label>
                        <select
                          required
                          value={categoryId}
                          onChange={(e) => setCategoryId(e.target.value)}
                          onBlur={() => handleBlur('categoryId')}
                          className={getInputClass('categoryId')}
                        >
                          <option value="">Select Individual Category</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </select>
                        {touched.categoryId && fieldErrors.categoryId && (
                          <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                            <AlertCircle size={12} className="shrink-0" /> {fieldErrors.categoryId}
                          </p>
                        )}
                      </div>

                      {/* 5. State */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">State *</label>
                        <select
                          required
                          value={stateName}
                          onChange={handleStateSelectChange}
                          onBlur={() => handleBlur('stateName')}
                          className={getInputClass('stateName')}
                        >
                          <option value="">Select State</option>
                          {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {touched.stateName && fieldErrors.stateName && (
                          <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                            <AlertCircle size={12} className="shrink-0" /> {fieldErrors.stateName}
                          </p>
                        )}
                      </div>

                      {/* 6. City */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">City *</label>
                        <select
                          required
                          value={cityName}
                          onChange={(e) => setCityName(e.target.value)}
                          onBlur={() => handleBlur('cityName')}
                          disabled={!stateName}
                          className={getInputClass('cityName')}
                        >
                          <option value="">{stateName ? "Select City" : "Select State First"}</option>
                          {cityName && !availableCities.includes(cityName) && (
                            <option value={cityName}>{cityName}</option>
                          )}
                          {availableCities.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        {touched.cityName && fieldErrors.cityName && (
                          <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                            <AlertCircle size={12} className="shrink-0" /> {fieldErrors.cityName}
                          </p>
                        )}
                      </div>

                      {/* 7. Area */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Area *</label>
                        <input
                          type="text"
                          required
                          placeholder="Locality / Area"
                          value={area}
                          onChange={(e) => setArea(e.target.value)}
                          onBlur={() => handleBlur('area')}
                          className={getInputClass('area')}
                        />
                        {touched.area && fieldErrors.area && (
                          <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                            <AlertCircle size={12} className="shrink-0" /> {fieldErrors.area}
                          </p>
                        )}
                      </div>

                      {/* 8. Legal Documents Proof */}
                      <div className={`p-3 border rounded-lg bg-slate-50 ${touched.govtProofFile && fieldErrors.govtProofFile ? 'border-rose-500' : 'border-slate-200'}`}>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Legal Document Proof (Aadhaar / PAN Card) *</label>
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => {
                            handleGovtProofChange(e);
                            setTouched(prev => ({ ...prev, govtProofFile: true }));
                          }}
                          className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#2ab391]/10 file:text-[#2ab391] file:cursor-pointer"
                        />
                        {govtProofPreview && (
                          <a href={govtProofPreview} target="_blank" rel="noreferrer" className="mt-2 block text-xs text-[#2ab391] font-bold hover:underline truncate">
                            View Uploaded Document
                          </a>
                        )}
                        {touched.govtProofFile && fieldErrors.govtProofFile && (
                          <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                            <AlertCircle size={12} className="shrink-0" /> {fieldErrors.govtProofFile}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Form Buttons for Individual Business */}
                    <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-6">
                      <button
                        type="button"
                        onClick={resetFormFields}
                        className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
                      >
                        Reset Form
                      </button>
                      <button
                        type="submit"
                        disabled={submittingDraft || selectedVendor.hasPendingDraft}
                        className={`px-5 py-2 rounded-lg text-sm text-white font-bold transition flex items-center gap-2 ${
                          selectedVendor.hasPendingDraft
                            ? 'bg-slate-300 cursor-not-allowed'
                            : 'bg-[#2ab391] hover:bg-[#239c7e] shadow-sm'
                        }`}
                      >
                        {submittingDraft ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          'Submit Draft to Vendor'
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Standard Company Form Layout */
                  <div className="space-y-6">
                    {/* Base Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Company Name *</label>
                    <input
                      type="text"
                      required
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      onBlur={() => handleBlur('companyName')}
                      className={getInputClass('companyName')}
                    />
                    {touched.companyName && fieldErrors.companyName && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.companyName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Target Vendor Email (Readonly)</label>
                    <input
                      type="text"
                      disabled
                      value={email}
                      className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Phone Number *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 9876543210"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      onBlur={() => handleBlur('phone')}
                      className={getInputClass('phone')}
                    />
                    {touched.phone && fieldErrors.phone && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">
                      GSTIN Number <span className="text-emerald-600 font-normal text-xs normal-case ml-1 font-sans">(Preferred for trust)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="GSTIN"
                      value={gstin}
                      onChange={(e) => setGstin(e.target.value)}
                      onBlur={() => handleBlur('gstin')}
                      className={getInputClass('gstin')}
                    />
                    {touched.gstin && fieldErrors.gstin && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.gstin}
                      </p>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">About Company *</label>
                  <textarea
                    required
                    rows={3}
                    value={aboutCompany}
                    onChange={(e) => setAboutCompany(e.target.value)}
                    onBlur={() => handleBlur('aboutCompany')}
                    className={getInputClass('aboutCompany', true)}
                  />
                  {touched.aboutCompany && fieldErrors.aboutCompany && (
                    <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                      <AlertCircle size={12} className="shrink-0" /> {fieldErrors.aboutCompany}
                    </p>
                  )}
                </div>

                {/* Address Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Address Line *</label>
                    <input
                      type="text"
                      required
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      onBlur={() => handleBlur('address')}
                      className={getInputClass('address')}
                    />
                    {touched.address && fieldErrors.address && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.address}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">State *</label>
                    <select
                      required
                      value={stateName}
                      onChange={handleStateSelectChange}
                      onBlur={() => handleBlur('stateName')}
                      className={getInputClass('stateName')}
                    >
                      <option value="">Select State</option>
                      {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {touched.stateName && fieldErrors.stateName && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.stateName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">City *</label>
                    <select
                      required
                      value={cityName}
                      onChange={(e) => setCityName(e.target.value)}
                      onBlur={() => handleBlur('cityName')}
                      disabled={!stateName}
                      className={getInputClass('cityName')}
                    >
                      <option value="">{stateName ? "Select City" : "Select State First"}</option>
                      {cityName && !availableCities.includes(cityName) && (
                        <option value={cityName}>{cityName}</option>
                      )}
                      {availableCities.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    {touched.cityName && fieldErrors.cityName && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.cityName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Zipcode *</label>
                    <input
                      type="text"
                      required
                      maxLength={6}
                      placeholder="6 digits"
                      value={zipcode}
                      onChange={(e) => setZipcode(e.target.value)}
                      onBlur={() => handleBlur('zipcode')}
                      className={getInputClass('zipcode')}
                    />
                    {touched.zipcode && fieldErrors.zipcode && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.zipcode}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Area / Locality *</label>
                    <input
                      type="text"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      onBlur={() => handleBlur('area')}
                      className={getInputClass('area')}
                    />
                    {touched.area && fieldErrors.area && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.area}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Website URL</label>
                    <input
                      type="url"
                      placeholder="https://example.com"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Established Since (Year)</label>
                    <input
                      type="number"
                      placeholder="e.g. 2015"
                      value={establishedSince}
                      onChange={(e) => setEstablishedSince(e.target.value)}
                      onBlur={() => handleBlur('establishedSince')}
                      className={getInputClass('establishedSince')}
                    />
                    {touched.establishedSince && fieldErrors.establishedSince && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.establishedSince}
                      </p>
                    )}
                  </div>
                </div>

                {/* Category & Tags Select */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Category *</label>
                    <select
                      required
                      value={categoryId}
                      onChange={(e) => {
                        setCategoryId(e.target.value);
                        setSelectedTags([]);
                        setSelectedBrands([]);
                        setTouched(prev => ({ ...prev, categoryId: true }));
                      }}
                      onBlur={() => handleBlur('categoryId')}
                      className={getInputClass('categoryId')}
                    >
                      <option value="">Select Category</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {touched.categoryId && fieldErrors.categoryId && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.categoryId}
                      </p>
                    )}
                  </div>
                  
                  {/* Tags Panel */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Category Tags * (Select at least one)</label>
                    <div className={`max-h-28 overflow-y-auto border rounded-lg p-3 grid grid-cols-2 gap-2 bg-slate-50 ${touched.selectedTags && fieldErrors.selectedTags ? 'border-rose-500' : 'border-slate-200'}`}>
                      {tags.length === 0 ? (
                        <span className="text-xs text-slate-400 col-span-2">Select a category first to load tags.</span>
                      ) : (
                        tags.map(t => (
                          <label key={t.id} className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedTags.includes(t.id)}
                              onChange={() => {
                                handleTagToggle(t.id);
                                setTouched(prev => ({ ...prev, selectedTags: true }));
                              }}
                              className="rounded text-[#2ab391] focus:ring-[#2ab391]"
                            />
                            {t.name}
                          </label>
                        ))
                      )}
                    </div>
                    {touched.selectedTags && fieldErrors.selectedTags && (
                      <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} className="shrink-0" /> {fieldErrors.selectedTags}
                      </p>
                    )}
                  </div>
                </div>

                {/* Brands Select */}
                {categoryId && brands.length > 0 && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Linked Brands</label>
                    <div className="max-h-28 overflow-y-auto border border-slate-200 rounded-lg p-3 grid grid-cols-3 gap-2 bg-slate-50">
                      {brands.map(b => (
                        <label key={b.id} className="flex items-center gap-2 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedBrands.includes(b.id)}
                            onChange={() => handleBrandToggle(b.id)}
                            className="rounded text-[#2ab391] focus:ring-[#2ab391]"
                          />
                          {b.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Business Hours Builder */}
                <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <label className="text-sm font-bold text-slate-800">Business Hours Setup</label>
                    <select
                      value={bhType}
                      onChange={(e) => setBhType(e.target.value)}
                      className="px-3 py-1.5 border border-slate-200 bg-white rounded-lg text-xs font-semibold focus:outline-none"
                    >
                      <option value="MON_TO_FRI">Monday to Friday</option>
                      <option value="MON_TO_SAT">Monday to Saturday</option>
                      <option value="ALL_DAYS">All 7 Days</option>
                      <option value="CUSTOM">Custom Schedule</option>
                    </select>
                  </div>

                  {bhType !== 'CUSTOM' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1">Open Time</label>
                        <select
                          value={bhMainOpen}
                          onChange={(e) => setBhMainOpen(e.target.value)}
                          className="w-full px-3 py-1.5 border border-slate-200 bg-white rounded-lg text-sm"
                        >
                          {timeOptions.map(t => <option key={`main-o-${t}`} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1">Close Time</label>
                        <select
                          value={bhMainClose}
                          onChange={(e) => setBhMainClose(e.target.value)}
                          className="w-full px-3 py-1.5 border border-slate-200 bg-white rounded-lg text-sm"
                        >
                          {timeOptions.map(t => <option key={`main-c-${t}`} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {bhCustom.map((row, idx) => (
                        <div key={`custom-hr-${idx}`} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-200 text-xs">
                          <select
                            value={row.day}
                            onChange={(e) => updateCustomHourRow(idx, 'day', e.target.value)}
                            className="px-2 py-1 border border-slate-200 rounded"
                          >
                            {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => (
                              <option key={d} value={d}>{d}</option>
                            ))}
                          </select>
                          <span className="text-slate-400">from</span>
                          <select
                            value={row.openTime}
                            onChange={(e) => updateCustomHourRow(idx, 'openTime', e.target.value)}
                            className="px-2 py-1 border border-slate-200 rounded"
                          >
                            {timeOptions.map(t => <option key={`custom-o-${idx}-${t}`} value={t}>{t}</option>)}
                          </select>
                          <span className="text-slate-400">to</span>
                          <select
                            value={row.closeTime}
                            onChange={(e) => updateCustomHourRow(idx, 'closeTime', e.target.value)}
                            className="px-2 py-1 border border-slate-200 rounded"
                          >
                            {timeOptions.map(t => <option key={`custom-c-${idx}-${t}`} value={t}>{t}</option>)}
                          </select>
                          <button
                            type="button"
                            onClick={() => removeCustomHourRow(idx)}
                            className="p-1 hover:bg-rose-50 text-rose-500 rounded ml-auto"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addCustomHourRow}
                        className="text-xs flex items-center gap-1 text-[#2ab391] font-bold hover:underline"
                      >
                        <Plus className="w-4 h-4" /> Add Custom Day
                      </button>
                    </div>
                  )}
                </div>

                {/* Social Links */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Instagram Profile</label>
                    <input
                      type="text"
                      placeholder="Profile username or URL"
                      value={instagram}
                      onChange={(e) => setInstagram(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Facebook URL</label>
                    <input
                      type="text"
                      placeholder="Facebook Page URL"
                      value={facebook}
                      onChange={(e) => setFacebook(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">LinkedIn Profile</label>
                    <input
                      type="text"
                      placeholder="Profile username or URL"
                      value={linkedin}
                      onChange={(e) => setLinkedin(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">X Handle</label>
                    <input
                      type="text"
                      placeholder="username"
                      value={twitter}
                      onChange={(e) => setTwitter(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]"
                    />
                  </div>
                </div>

                {/* File Upload Section */}
                <div className="space-y-4 border-t border-slate-100 pt-6">
                  <h3 className="text-sm font-bold text-slate-800">Media Uploads</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Banner File */}
                    <div className={`p-3 border rounded-lg bg-slate-50 ${touched.bannerFile && fieldErrors.bannerFile ? 'border-rose-500' : 'border-slate-200'}`}>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Profile Banner *</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          handleBannerChange(e);
                          setTouched(prev => ({ ...prev, bannerFile: true }));
                        }}
                        className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#2ab391]/10 file:text-[#2ab391] file:cursor-pointer"
                      />
                      {bannerPreview && (
                        <img src={bannerPreview} alt="Banner Preview" className="mt-2 w-full h-16 object-cover rounded border border-slate-200" />
                      )}
                      {touched.bannerFile && fieldErrors.bannerFile && (
                        <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                          <AlertCircle size={12} className="shrink-0" /> {fieldErrors.bannerFile}
                        </p>
                      )}
                    </div>

                    {/* Logo File */}
                    <div className={`p-3 border rounded-lg bg-slate-50 ${touched.logoFile && fieldErrors.logoFile ? 'border-rose-500' : 'border-slate-200'}`}>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Company Logo *</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          handleLogoChange(e);
                          setTouched(prev => ({ ...prev, logoFile: true }));
                        }}
                        className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#2ab391]/10 file:text-[#2ab391] file:cursor-pointer"
                      />
                      {logoPreview && (
                        <img src={logoPreview} alt="Logo Preview" className="mt-2 w-12 h-12 object-cover rounded-full border border-slate-200 mx-auto" />
                      )}
                      {touched.logoFile && fieldErrors.logoFile && (
                        <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                          <AlertCircle size={12} className="shrink-0" /> {fieldErrors.logoFile}
                        </p>
                      )}
                    </div>

                    {/* Govt Proof File */}
                    <div className={`p-3 border rounded-lg bg-slate-50 ${touched.govtProofFile && fieldErrors.govtProofFile ? 'border-rose-500' : 'border-slate-200'}`}>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Govt ID / GST Photo Proof *</label>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          handleGovtProofChange(e);
                          setTouched(prev => ({ ...prev, govtProofFile: true }));
                        }}
                        className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#2ab391]/10 file:text-[#2ab391] file:cursor-pointer"
                      />
                      {govtProofPreview && (
                        <a href={govtProofPreview} target="_blank" rel="noreferrer" className="mt-2 block text-xs text-[#2ab391] font-bold hover:underline truncate">
                          View Uploaded Document
                        </a>
                      )}
                      {touched.govtProofFile && fieldErrors.govtProofFile && (
                        <p className="text-xs text-rose-500 mt-1 flex items-center gap-1 font-medium">
                          <AlertCircle size={12} className="shrink-0" /> {fieldErrors.govtProofFile}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Gallery Section — Paid Only */}
                  {vendorMembership !== 'FREE' ? (
                    <div className="p-4 border border-slate-200 rounded-xl bg-slate-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">Gallery Images</label>
                          <p className="text-[10px] text-slate-400">Available on your Active paid plan ({vendorMembership}).</p>
                        </div>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleGalleryChange}
                          id="gallery-upload"
                          className="hidden"
                        />
                        <label 
                          htmlFor="gallery-upload"
                          className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold hover:bg-slate-100 cursor-pointer flex items-center gap-1 shadow-sm"
                        >
                          <Plus className="w-3.5 h-3.5" /> Upload Photos
                        </label>
                      </div>

                      {/* Display Existing Gallery images */}
                      {existingGalleryUrls.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 mb-1 uppercase">Existing Gallery Images (Click to delete):</p>
                          <div className="flex flex-wrap gap-2">
                            {existingGalleryUrls.map((url, idx) => (
                              <div key={`exist-img-${idx}`} className="relative group w-14 h-14 rounded overflow-hidden border border-slate-200 bg-white">
                                <img src={getImageUrl(url)} alt="Exist gallery" className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => removeExistingGalleryFile(url)}
                                  className="absolute inset-0 bg-rose-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Selected files previews */}
                      {galleryPreviews.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 mb-1 uppercase">New Photos to Upload:</p>
                          <div className="flex flex-wrap gap-2">
                            {galleryPreviews.map((preview, idx) => (
                              <div key={`new-img-${idx}`} className="relative group w-14 h-14 rounded overflow-hidden border border-slate-200 bg-white">
                                <img src={preview} alt="New gallery" className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => removeSelectedGalleryFile(idx)}
                                  className="absolute inset-0 bg-rose-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 border border-dashed border-slate-200 rounded-lg bg-slate-50 flex items-center gap-2 text-xs text-slate-400">
                      <AlertCircle className="w-4 h-4 text-slate-400" />
                      <span>Gallery upload is disabled. Gallery photos are only supported for vendors on professional/premium membership plans.</span>
                    </div>
                  )}
                </div>

                {/* Form Buttons */}
                <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-6">
                  <button
                    type="button"
                    onClick={resetFormFields}
                    className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
                  >
                    Reset Form
                  </button>
                  <button
                    type="submit"
                    disabled={submittingDraft || selectedVendor.hasPendingDraft}
                    className={`px-5 py-2 rounded-lg text-sm text-white font-bold transition flex items-center gap-2 ${
                      selectedVendor.hasPendingDraft
                        ? 'bg-slate-300 cursor-not-allowed'
                        : 'bg-[#2ab391] hover:bg-[#239c7e] shadow-sm'
                    }`}
                  >
                    {submittingDraft ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Draft to Vendor'
                    )}
                  </button>
                </div>
                  </div>
                )}
              </form>
            )}
          </div>
        )}

        {/* Draft History Collapsible Panel */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setHistoryOpen(!historyOpen)}
            className="w-full flex items-center justify-between p-4 bg-slate-100 font-semibold text-slate-800 text-left"
          >
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-500" />
              Listing Draft Submission History Logs
            </span>
            {historyOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {historyOpen && (
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-400 text-xs font-bold uppercase">
                    <th className="p-3">Vendor</th>
                    <th className="p-3">Action</th>
                    <th className="p-3">Admin</th>
                    <th className="p-3">Submitted At</th>
                    <th className="p-3">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-600">
                  {drafts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-slate-400">No draft history records found.</td>
                    </tr>
                  ) : (
                    drafts.map(draft => (
                      <tr key={draft.id} className="hover:bg-slate-50/50">
                        <td className="p-3">
                          <p className="font-semibold text-slate-800">{draft.companyName}</p>
                          <p className="text-xs text-slate-400">{draft.vendorEmail}</p>
                        </td>
                        <td className="p-3">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${
                            draft.actionType === 'CREATE'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                          }`}>
                            {draft.actionType}
                          </span>
                        </td>
                        <td className="p-3 text-xs">{draft.adminEmail}</td>
                        <td className="p-3 text-xs">{draft.createdAt}</td>
                        <td className="p-3">
                          <div className="flex flex-col gap-0.5">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase w-fit ${
                              draft.status === 'PENDING_VENDOR_APPROVAL'
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : draft.status === 'APPROVED'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : draft.status === 'REJECTED'
                                ? 'bg-rose-50 text-rose-700 border-rose-200'
                                : 'bg-slate-50 text-slate-600 border-slate-200'
                            }`}>
                              {draft.status.replace(/_/g, ' ')}
                            </span>
                            {draft.status === 'REJECTED' && draft.rejectReason && (
                              <span className="text-[10px] text-rose-600 max-w-xs truncate italic" title={draft.rejectReason}>
                                Reason: "{draft.rejectReason}"
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          {draft.status === 'PENDING_VENDOR_APPROVAL' && (
                            <button
                              type="button"
                              onClick={() => handleCancelDraftClick(draft.id)}
                              className="text-rose-500 hover:text-rose-700 font-semibold text-xs flex items-center gap-1 justify-end ml-auto"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Cancel Draft
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              
              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 border-t border-slate-100 pt-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Show</span>
                  <div className="relative flex items-center">
                    <select
                      value={draftsPageSize}
                      onChange={(e) => {
                        const newSize = Number(e.target.value);
                        setDraftsPageSize(newSize);
                        loadDrafts(0, newSize);
                      }}
                      className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                      <ChevronDown size={12} />
                    </span>
                  </div>
                  <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Entries</span>
                  <button
                    type="button"
                    onClick={() => loadDrafts(draftsPagination.page)}
                    title="Reload current page"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-[#2ab391] hover:border-[#2ab391]/40 hover:bg-teal-50/50 rounded-xl font-bold text-xs transition-all cursor-pointer shadow-xs"
                  >
                    <RotateCw size={12} />
                    Reload
                  </button>
                </div>

                {draftsPagination.totalPages > 0 && (
                  <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => loadDrafts(Math.max(0, draftsPagination.page - 1))}
                      disabled={draftsPagination.page === 0}
                      className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center text-xs"
                    >
                      Previous
                    </button>

                    <div className="hidden sm:flex items-center gap-1">
                      {getPageNumbers(draftsPagination.page, draftsPagination.totalPages).map((pageNum, idx) => {
                        if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                          return (
                            <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                              ...
                            </span>
                          );
                        }
                        const isActive = draftsPagination.page === pageNum;
                        return (
                          <button
                            key={`page-${pageNum}`}
                            type="button"
                            onClick={() => loadDrafts(pageNum)}
                            className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                              ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                              : 'bg-white border-gray-200 text-gray-750 hover:bg-gray-50'
                              }`}
                          >
                            {pageNum + 1}
                          </button>
                        );
                      })}
                    </div>

                    <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-xs min-w-[70px] text-center">
                      {draftsPagination.page + 1} / {draftsPagination.totalPages}
                    </span>

                    <button
                      type="button"
                      onClick={() => loadDrafts(Math.min(draftsPagination.totalPages - 1, draftsPagination.page + 1))}
                      disabled={draftsPagination.page >= draftsPagination.totalPages - 1}
                      className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center text-xs"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-xl border border-slate-100 max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-150 text-left">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Draft Submission
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              You are about to submit a proposed listing <strong>{isEditMode ? 'UPDATE' : 'CREATE'}</strong> draft for company <strong>"{companyName}"</strong>.
            </p>
            <p className="text-xs text-slate-500 mt-2 bg-amber-50 p-2 border border-amber-100 rounded-lg">
              The vendor <strong>{selectedVendor.emailId}</strong> will receive an email notification. They must review and approve these details before they become live in the directory.
            </p>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmitDraft}
                className="px-5 py-2 bg-[#2ab391] hover:bg-[#239c7e] text-white font-bold rounded-lg text-sm transition shadow-sm"
              >
                Yes, Submit Draft
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Draft Confirmation Modal */}
      {cancelDraftId && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-xl border border-slate-100 max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-150 text-left">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-rose-500" />
              Withdraw Pending Draft
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              Are you sure you want to cancel and withdraw this pending listing draft?
            </p>
            <p className="text-xs text-rose-500 mt-2 bg-rose-50 p-2 border border-rose-100 rounded-lg">
              This action will withdraw the notification from the vendor, delete all temporary uploaded files from the storage server, and cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setCancelDraftId(null)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={handleCancelDraftConfirm}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg text-sm transition shadow-sm"
              >
                Yes, Withdraw Draft
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Deletion Step 1 Modal */}
      {showAdminDeleteStep1Modal && selectedVendor && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md w-full p-5 sm:p-6 animate-in fade-in zoom-in-95 duration-150 text-left">
            <div className="flex items-center gap-3 text-rose-600 mb-3">
              <div className="w-10 h-10 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-slate-800">
                Delete Vendor Listing?
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              Are you sure you want to delete the approved listing for vendor <strong>"{selectedVendor.name}"</strong> ({selectedVendor.emailId})?
            </p>
            <p className="text-xs text-amber-800 bg-amber-50/80 p-3 rounded-xl border border-amber-200/60 mt-3 font-medium">
              This action will notify the vendor on their portal so they can create a new listing if needed.
            </p>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowAdminDeleteStep1Modal(false);
                }}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs sm:text-sm font-semibold hover:bg-slate-50 transition text-center cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAdminDeleteStep1Modal(false);
                  setShowAdminDeleteStep2Modal(true);
                }}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-xs sm:text-sm transition shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
              >
                Next: Confirm Deletion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Deletion Step 2 Modal */}
      {showAdminDeleteStep2Modal && selectedVendor && (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md w-full p-5 sm:p-6 animate-in fade-in zoom-in-95 duration-150 text-left">
            <div className="flex items-center gap-3 text-rose-600 mb-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 border border-rose-200 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-rose-600 animate-pulse" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900">
                  Confirm Permanent Deletion
                </h3>
                <p className="text-[11px] text-rose-600 font-bold uppercase tracking-wider">Step 2 of 2 — Administrative Warning</p>
              </div>
            </div>

            <div className="p-3.5 bg-rose-50 border border-rose-200/80 rounded-xl text-xs text-rose-900 space-y-2 mt-2">
              <strong className="block font-bold text-rose-950 flex items-center gap-1">
                <AlertCircle size={14} className="text-rose-600 shrink-0" />
                PERMANENT ACTION WARNING:
              </strong>
              <p className="leading-relaxed text-rose-800">
                This action <strong>CANNOT be undone</strong>. Deleting this listing will permanently remove:
              </p>
              <ul className="list-disc list-inside space-y-1 text-rose-800 text-[11px] font-semibold pl-1">
                <li>Vendor database profile & history</li>
                <li>Uploaded logo, profile banner & legal documents</li>
                <li>All uploaded gallery photos & attachments</li>
              </ul>
            </div>

            <div className="mt-4 space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                To confirm permanent deletion, please type <span className="text-rose-600 font-extrabold">DELETE</span> below:
              </label>
              <input
                type="text"
                value={adminDeleteConfirmInput}
                onChange={(e) => setAdminDeleteConfirmInput(e.target.value)}
                placeholder='Type "DELETE"'
                className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs sm:text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500 uppercase tracking-wider"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 mt-6">
              <button
                type="button"
                disabled={adminDeletingInProgress}
                onClick={() => {
                  setShowAdminDeleteStep2Modal(false);
                  setShowAdminDeleteStep1Modal(true);
                  setAdminDeleteConfirmInput('');
                }}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs sm:text-sm font-semibold hover:bg-slate-50 transition text-center cursor-pointer"
              >
                Go Back
              </button>
              <button
                type="button"
                disabled={adminDeletingInProgress || adminDeleteConfirmInput.trim().toUpperCase() !== 'DELETE'}
                onClick={handleAdminDeleteVendorListing}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold rounded-xl text-xs sm:text-sm transition shadow-md shadow-rose-600/20 flex items-center justify-center gap-1.5 cursor-pointer"
              >
                {adminDeletingInProgress ? 'Deleting Media & Data...' : 'Permanently Delete Vendor Listing'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default VendorListingManagerTab;
